import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { X, Download, Smartphone } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export function PWAInstallPrompt() {
    const { language } = useLanguage();
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showPrompt, setShowPrompt] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [isInstalled, setIsInstalled] = useState(false);

    useEffect(() => {
        // Check if already installed
        if (window.matchMedia('(display-mode: standalone)').matches) {
            setIsInstalled(true);
            return;
        }

        // Check if iOS
        const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        setIsIOS(isIOSDevice);

        // Listen for beforeinstallprompt event (Chrome, Edge, etc.)
        const handler = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            
            // Show prompt after 30 seconds or if user has logged in
            const hasSeenPrompt = localStorage.getItem('pwa-prompt-seen');
            if (!hasSeenPrompt) {
                setTimeout(() => setShowPrompt(true), 30000);
            }
        };

        window.addEventListener('beforeinstallprompt', handler);

        // Check if should show iOS prompt
        if (isIOSDevice && !localStorage.getItem('pwa-prompt-seen')) {
            setTimeout(() => setShowPrompt(true), 30000);
        }

        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    const handleInstall = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            await deferredPrompt.userChoice;
            setDeferredPrompt(null);
        }
        setShowPrompt(false);
        localStorage.setItem('pwa-prompt-seen', 'true');
    };

    const handleDismiss = () => {
        setShowPrompt(false);
        localStorage.setItem('pwa-prompt-seen', 'true');
    };

    if (isInstalled || !showPrompt) return null;

    const t = {
        de: {
            title: 'BESTBUDDY installieren',
            description: 'Installiere die App auf deinem Homescreen für schnellen Zugriff und Offline-Nutzung!',
            install: 'Installieren',
            later: 'Später',
            iosTitle: 'Zum Homescreen hinzufügen',
            iosStep1: '1. Tippe auf',
            iosStep2: '(Teilen-Symbol)',
            iosStep3: '2. Scrolle nach unten',
            iosStep4: '3. Tippe auf "Zum Home-Bildschirm"'
        },
        en: {
            title: 'Install BESTBUDDY',
            description: 'Install the app on your homescreen for quick access and offline use!',
            install: 'Install',
            later: 'Later',
            iosTitle: 'Add to Home Screen',
            iosStep1: '1. Tap the',
            iosStep2: '(Share icon)',
            iosStep3: '2. Scroll down',
            iosStep4: '3. Tap "Add to Home Screen"'
        }
    }[language] || {
        de: {}
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0, y: 100 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 100 }}
                className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:w-96"
            >
                <div className="bg-card rounded-2xl shadow-2xl border border-border overflow-hidden">
                    {/* Header */}
                    <div className="bg-primary p-4 flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                            <Smartphone className="w-5 h-5 text-primary-foreground" />
                        </div>
                        <div className="flex-1">
                            <h3 className="font-bold text-primary-foreground text-sm">{t.title}</h3>
                            <p className="text-xs text-primary-foreground/70">Nanouk empfiehlt</p>
                        </div>
                        <button 
                            onClick={handleDismiss}
                            className="text-primary-foreground/60 hover:text-primary-foreground p-1"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-4">
                        {isIOS ? (
                            <div className="space-y-2 text-sm">
                                <p className="font-medium text-foreground">{t.iosTitle}:</p>
                                <div className="space-y-1.5 text-muted-foreground">
                                    <p>1. {t.iosStep2}</p>
                                    <p>{t.iosStep3}</p>
                                    <p>{t.iosStep4}</p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <p className="text-muted-foreground text-sm mb-4">{t.description}</p>
                                <div className="flex gap-2">
                                    <Button 
                                        variant="ghost" 
                                        className="flex-1 text-muted-foreground"
                                        onClick={handleDismiss}
                                    >
                                        {t.later}
                                    </Button>
                                    <Button 
                                        className="flex-1 bg-gradient-to-r from-violet-600 to-indigo-600"
                                        onClick={handleInstall}
                                    >
                                        <Download className="w-4 h-4 mr-2" />
                                        {t.install}
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Nanouk badge */}
                    <div className="px-4 pb-4">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>🐺</span>
                            <span>Offline verfügbar • Push-Benachrichtigungen • Schnellzugriff</span>
                        </div>
                    </div>
                </div>
            </motion.div>
        </AnimatePresence>
    );
}
